import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useQuery } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { ClipboardCheckIcon, MicroscopeIcon, ScrollTextIcon } from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { useClassifier } from "@/hooks/useClassifier";
import { DEFAULT_CLASSIFICATION_LLM_MODEL_KEY, type LlmModelKey } from "@smartmed/llm-models";
import { useLlmModels } from "@/hooks/useLlmModels";
import { getPatientEncounterLabel } from "@/test-data/patientEncounterLabel";

const getIconForDataType = (dataType: string | null) => {
  switch (dataType) {
    case "Endoscopy":
      return (
        <TooltipProvider key={dataType}>
          <Tooltip>
            <TooltipTrigger asChild>
              <ClipboardCheckIcon className="w-4 h-4" />
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">Endoscopy Report</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    case "Histology":
      return (
        <TooltipProvider key={dataType}>
          <Tooltip>
            <TooltipTrigger asChild>
              <MicroscopeIcon className="w-4 h-4" />
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">Histology Report</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    case "GP Summary":
      return (
        <TooltipProvider key={dataType}>
          <Tooltip>
            <TooltipTrigger asChild>
              <ScrollTextIcon className="w-4 h-4" />
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">GP Summary</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    default:
      return null;
  }
};

interface TriggerPatientEncounterRunProps {
  open: boolean;
  onClose: () => void;
}

export function TriggerPatientEncounterRun({ open, onClose }: TriggerPatientEncounterRunProps) {
  const [selectedPatientEncounters, setSelectedPatientEncounters] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedLlmModelId, setSelectedLlmModelId] = useState<LlmModelKey | undefined>(undefined);
  const { classifyEncounter } = useClassifier();

  // Query: fetch patients with at least one encounter with at least one medical_text_example
  // this means they are a test patient...
  const {
    data: patientEncounters,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["test-patients-with-encounters"],
    queryFn: async () => {
      const result = await supabase.from("patient_encounters").select(`
             *,
             patient:patients(*),
             patient_encounter_medical_text_examples!inner(
              *,
              medical_text_examples!inner(*)
             )
          `);

      if (result.error) throw result.error;

      return result.data;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const { data: llmModels, isLoading: isLlmModelsLoading, error: llmModelsError } = useLlmModels();

  useEffect(() => {
    if (error) {
      toast.error("Failed to fetch patients");
    }
  }, [error]);

  useEffect(() => {
    if (llmModels?.some((model) => model.id === DEFAULT_CLASSIFICATION_LLM_MODEL_KEY)) {
      setSelectedLlmModelId(DEFAULT_CLASSIFICATION_LLM_MODEL_KEY);
    }
  }, [llmModels]);

  useEffect(() => {
    if (llmModelsError) {
      toast.error("Failed to fetch LLM models");
    }
  }, [llmModelsError]);

  const togglePatient = (patientId: string) => {
    setSelectedPatientEncounters((prev) =>
      prev.includes(patientId) ? prev.filter((id) => id !== patientId) : [...prev, patientId],
    );
  };

  const toggleSelectAll = () => {
    if (!patientEncounters) return;
    if (selectedPatientEncounters.length === patientEncounters.length) {
      setSelectedPatientEncounters([]);
    } else {
      setSelectedPatientEncounters(patientEncounters.map((p) => p.id));
    }
  };

  const handleSubmit = async () => {
    if (selectedPatientEncounters.length === 0) {
      toast.error("Please select at least one patient encounter");
      return;
    }
    if (!selectedLlmModelId) {
      toast.error("Please select an LLM model");
      return;
    }
    setIsSubmitting(true);
    try {
      const results = await Promise.allSettled(
        selectedPatientEncounters.map((encounterId) =>
          classifyEncounter({ encounterId, llmModelId: selectedLlmModelId }),
        ),
      );

      const failedRuns = results.filter((result) => result.status === "rejected" || !!result.value.error);
      const successfulRuns = results.filter((result) => result.status === "fulfilled" && !result.value.error);

      failedRuns.forEach((result) => {
        toast.error(`Failed to start run: ${(result as PromiseRejectedResult).reason}`);
      });

      if (successfulRuns.length > 0) {
        toast.success(`Started ${successfulRuns.length} classification run${successfulRuns.length > 1 ? "s" : ""}`);
      }

      onClose();
    } catch (error) {
      console.error("Error triggering runs:", error);
      toast.error("Failed to trigger classification runs");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!patientEncounters?.length) {
    return null;
  }

  if (!open) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Select Patients</DialogTitle>
          <DialogDescription>
            Choose patients who have at least one encounter with medical text examples.
          </DialogDescription>
        </DialogHeader>
        {isLoading || isLlmModelsLoading ? (
          <div className="flex items-center justify-center h-40 flex-1">Loading...</div>
        ) : (
          <div className="flex flex-col gap-4 flex-1 min-h-0">
            <div className="mb-4 flex items-center space-x-4">
              <label className="text-sm font-medium">LLM Model:</label>
              <Select
                value={selectedLlmModelId}
                onValueChange={(value) => setSelectedLlmModelId(value as LlmModelKey)}
                disabled={isLlmModelsLoading || !llmModels}
              >
                <SelectTrigger className="w-64">
                  <SelectValue placeholder="Select a model" />
                </SelectTrigger>
                <SelectContent>
                  {llmModels?.map((model) => (
                    <SelectItem key={model.id} value={model.id}>
                      {model.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 overflow-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-background z-10">
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox
                        checked={
                          patientEncounters &&
                          selectedPatientEncounters.length === patientEncounters.length &&
                          patientEncounters.length > 0
                        }
                        onCheckedChange={toggleSelectAll}
                      />
                    </TableHead>
                    <TableHead>Patient</TableHead>
                    <TableHead>Encounter</TableHead>
                    <TableHead>Reports available</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {patientEncounters?.map((patientEncounter) => (
                    <TableRow key={patientEncounter.id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedPatientEncounters.includes(patientEncounter.id)}
                          onCheckedChange={() => togglePatient(patientEncounter.id)}
                        />
                      </TableCell>
                      <TableCell>{patientEncounter.patient.id}</TableCell>
                      <TableCell>{getPatientEncounterLabel(patientEncounter)}</TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          {patientEncounter.patient_encounter_medical_text_examples
                            .flatMap((i) => i.medical_text_examples)
                            .map((i) => i.test_type)
                            .sort()
                            .map((testType) => getIconForDataType(testType))}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="flex justify-between items-center pt-4 border-t flex-shrink-0">
              <div className="text-sm text-muted-foreground">
                {selectedPatientEncounters.length} encounters
                {selectedPatientEncounters.length !== 1 ? "s" : ""} selected
                <span className="ml-2">({patientEncounters.length} shown)</span>
              </div>
              <div className="flex space-x-2">
                <Button variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                <Button onClick={handleSubmit} disabled={isSubmitting || !selectedLlmModelId}>
                  {isSubmitting ? "Starting..." : "Start Run"}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
